import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../../context/AuthContext';
import {
  getMyWeeklyRankingUrl,
  getWeeklyRankingUrl,
} from '../../../api/generated/cat-runner/game-cat-runner/game-cat-runner.ts';
import ArrowLeftImage from '../../../assets/icons/arrow_left.png';
import forestBackground from '../../../assets/cat-running/backgrounds/forest-bg.png';
import titleTrophy from '../../../assets/cat-running/ranking/title-trophy.png';
import podiumFirstBase from '../../../assets/cat-running/ranking/podium-first-base.svg';
import podiumFirstStroke from '../../../assets/cat-running/ranking/podium-first-stroke.svg';
import podiumFirstMedal from '../../../assets/cat-running/ranking/podium-first-medal.png';
import podiumSecondBase from '../../../assets/cat-running/ranking/podium-second-base.svg';
import podiumSecondStroke from '../../../assets/cat-running/ranking/podium-second-stroke.svg';
import podiumSecondMedal from '../../../assets/cat-running/ranking/podium-second-medal.png';
import podiumThirdBase from '../../../assets/cat-running/ranking/podium-third-base.svg';
import podiumThirdStroke from '../../../assets/cat-running/ranking/podium-third-stroke.svg';
import podiumThirdMedal from '../../../assets/cat-running/ranking/podium-third-medal.png';
import CatRunningOutlineFrame from './CatRunningOutlineFrame';

const PODIUM_ASSETS = {
  1: {
    rankLabel: '1st',
    accentClassName: 'text-[#FFBC2B]',
    baseImage: podiumFirstBase,
    strokeImage: podiumFirstStroke,
    medalImage: podiumFirstMedal,
    sizeClassName: 'h-[127px] w-[80px]',
    wrapperClassName: 'left-1/2 top-[198px] -translate-x-1/2',
    podiumClassName: 'left-1/2 top-[310px] -translate-x-1/2',
  },
  2: {
    rankLabel: '2nd',
    accentClassName: 'text-[#D9D9D9]',
    baseImage: podiumSecondBase,
    strokeImage: podiumSecondStroke,
    medalImage: podiumSecondMedal,
    sizeClassName: 'h-[82px] w-[80px]',
    wrapperClassName: 'left-1/2 top-[245px] -translate-x-[150px]',
    podiumClassName: 'left-1/2 top-[358px] -translate-x-[150px]',
  },
  3: {
    rankLabel: '3rd',
    accentClassName: 'text-[#E18865]',
    baseImage: podiumThirdBase,
    strokeImage: podiumThirdStroke,
    medalImage: podiumThirdMedal,
    sizeClassName: 'h-[61px] w-[80px]',
    wrapperClassName: 'left-1/2 top-[265px] translate-x-[70px]',
    podiumClassName: 'left-1/2 top-[379px] translate-x-[70px]',
  },
};

const formatDistance = (bestDistanceM) => `${Number(bestDistanceM ?? 0).toLocaleString('ko-KR')}m`;
const formatRewardPoint = (rewardPoint) => `${Number(rewardPoint ?? 0).toLocaleString('ko-KR')}p`;
const formatNickname = (nickname) => {
  const name = nickname || '-';
  const chars = Array.from(name);
  if (chars.length < 6) return name;
  return `${chars.slice(0, 6).join('')}...`;
};

function PodiumProfile({ entry, assets }) {
  return (
    <>
      <div className={`absolute flex w-[80px] flex-col items-center ${assets.wrapperClassName}`}>
        <div className="flex w-full flex-col items-center gap-[2px] text-center">
          <span className={`font-['DungGeunMo'] text-[20px] leading-[1.4] ${assets.accentClassName}`}>
            {assets.rankLabel}
          </span>
          <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">{formatNickname(entry.nickname)}</span>
          <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">
            {formatDistance(entry.bestDistanceM)}
          </span>
          {/* TODO: restore rewardPoint display when ranking API includes it.
          <span className={`font-['DungGeunMo'] text-[16px] leading-[1.4] ${assets.accentClassName}`}>
            {formatRewardPoint(entry.rewardPoint)}
          </span>
          */}
        </div>
      </div>

      <div className={`absolute ${assets.podiumClassName}`}>
        <div className={`relative ${assets.sizeClassName}`}>
          <img src={assets.baseImage} alt="" className="block h-full w-full" />
          <img src={assets.strokeImage} alt="" className="pointer-events-none absolute inset-0 block h-full w-full" />
          <img
            src={assets.medalImage}
            alt=""
            className="absolute left-1/2 top-0 h-10 w-10 -translate-x-1/2 -translate-y-[1px]"
          />
        </div>
      </div>
    </>
  );
}

function RankingRow({ ranking, nickname, bestDistanceM, rewardPoint, isMe }) {
  return (
    <div className="px-4">
      <div
        className={`flex items-center justify-between border-b py-[10px] px-5 ${
          isMe ? 'border-white bg-[#8B68DD]' : 'border-[#D9D9D9]'
        }`}
      >
        <div className="flex min-w-0 items-center gap-[10px]">
          <div className="flex w-[30px] items-center justify-center">
            <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">{ranking}</span>
          </div>
          <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">{formatNickname(nickname)}</span>
        </div>
        <div className="flex items-center justify-end gap-4">
          <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">
            {formatDistance(bestDistanceM)}
          </span>
          {/* TODO: restore rewardPoint display when ranking API includes it.
          <span className="font-['DungGeunMo'] text-[16px] leading-[1.4] text-white">
            {formatRewardPoint(rewardPoint)}
          </span>
          */}
        </div>
      </div>
    </div>
  );
}

export default function CatRunningRankingScreen() {
  const navigate = useNavigate();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken } = useContext(AuthContext);
  const accessToken = useAccessToken();

  const fetcher = useCallback(async (...args) => fetchWithToken(...args), [fetchWithToken]);

  const { data: weeklyRankingResponse } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getWeeklyRankingUrl()}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const { data: myWeeklyRankingResponse } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getMyWeeklyRankingUrl()}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const weeklyRanking = weeklyRankingResponse ?? {};
  const myWeeklyRanking = myWeeklyRankingResponse ?? {};
  const rankingEntries = Array.isArray(weeklyRanking.entries) ? weeklyRanking.entries : [];
  const hasRankingEntries = rankingEntries.length > 0;

  const podiumEntries = useMemo(
    () =>
      [1, 2, 3]
        .map((ranking) => rankingEntries.find((entry) => entry.ranking === ranking))
        .filter(Boolean),
    [rankingEntries],
  );

  const rankingRows = useMemo(() => {
    const baseRows = rankingEntries.filter((entry) => Number(entry.ranking ?? 0) > 3);
    const hasMyRowInTopList = baseRows.some((entry) => entry.ranking === myWeeklyRanking.ranking);

    if (hasMyRowInTopList || !myWeeklyRanking.ranking) {
      return baseRows;
    }

    return [
      ...baseRows,
      {
        ranking: myWeeklyRanking.ranking,
        nickname: myWeeklyRanking.nickname,
        bestDistanceM: myWeeklyRanking.bestDistanceM,
        rewardPoint: myWeeklyRanking.rewardPoint,
        isMe: true,
      },
    ];
  }, [
    myWeeklyRanking.bestDistanceM,
    myWeeklyRanking.nickname,
    myWeeklyRanking.ranking,
    myWeeklyRanking.rewardPoint,
    rankingEntries,
  ]);

  const myRankingRewardPoint = myWeeklyRanking?.rewardPoint;
  const hasRankingRows = rankingRows.length > 0;

  return (
    <div className="min-h-screen bg-[#652419]">
      <div className="mx-auto min-h-screen w-full bg-[#652419]">
        <div className="relative mx-auto min-h-screen w-full overflow-x-hidden bg-[#652419]">
          <div className="relative mx-auto min-h-[1040px] w-full">
            <div className="absolute inset-x-0 z-20 flex h-[46px] items-center px-[16px]">
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
                onClick={() => navigate('/cat-running')}
                aria-label="뒤로가기"
              >
                <img src={ArrowLeftImage} alt="" className="h-6 w-6 object-contain brightness-0 invert" />
              </button>
            </div>

            <div className="absolute h-[1280px] w-[1000px]">
              <img src={forestBackground} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/30 to-transparent" />
            </div>

            <div className="absolute inset-x-0 top-[64px] z-10 px-[16px]">
              <div className="flex w-full items-center justify-center gap-[10px] bg-white px-[10px]">
                <img src={titleTrophy} alt="" className="h-8 w-8 object-contain" />
                <span className="font-['DungGeunMo'] text-[30px] leading-[1.4] text-black">주간랭킹</span>
              </div>
              <div className="mt-6 text-center font-['DungGeunMo'] text-5xl font-normal text-white">
                {/* TODO: restore my rewardPoint display when ranking API includes it.
                {myRankingRewardPoint != null ? `${Number(myRankingRewardPoint).toLocaleString()}P` : ''}
                */}
              </div>
            </div>

            <div className="mx-auto">
              {hasRankingEntries &&
                podiumEntries.map((entry) => (
                  <PodiumProfile key={entry.ranking} entry={entry} assets={PODIUM_ASSETS[entry.ranking]} />
                ))}

              <div className={`absolute inset-x-0 z-10 px-[10px] ${hasRankingEntries ? 'top-[436px]' : 'top-[140px]'}`}>
                {hasRankingRows ? (
                <CatRunningOutlineFrame
                  className="w-full"
                  contentClassName="px-[20px] py-[18px]"
                  fillClassName="bg-black/12 backdrop-blur-[1px]"
                  lineWidth={3}
                  cornerGap={3}
                  contentInset={3}
                  lineClassName="bg-white"
                >
                  <div className="flex flex-col py-3">
                    {rankingRows.map((row, index) => (
                      <RankingRow
                        key={`${row.ranking}-${index}`}
                        ranking={row.ranking}
                        nickname={row.nickname}
                        bestDistanceM={row.bestDistanceM}
                        rewardPoint={row.rewardPoint}
                        isMe={Boolean(row.isMe || row.ranking === myWeeklyRanking.ranking)}
                      />
                    ))}
                  </div>
                </CatRunningOutlineFrame>
                ) : !hasRankingEntries ? (
                  <div className="flex min-h-[230px] flex-col items-center justify-center px-6 text-center text-white">
                    <p className="m-0 font-['DungGeunMo'] text-[22px] leading-[1.5]">현재 랭킹이 없습니다.</p>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="mt-4 h-[60px] w-full rounded-[8px] border-0 bg-black font-['Pretendard'] text-[16px] font-bold leading-[1.4] text-white"
                  onClick={() => navigate('/cat-running')}
                >
                  돌아가기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
